import type { AnonymizationRequest, AnonymizerResult, AnonymizerUsageResult, PersonalizationRequest, PersonalizationResponse } from './types';

export type WorkspaceRequestError = Error & {
    status?: number;
    usage?: AnonymizerUsageResult | null;
};

export type AnonymizerAuthResponse = { token: string; email: string };

export async function login(email: string, password: string): Promise<AnonymizerAuthResponse> {
    return parseJsonResponse<AnonymizerAuthResponse>(await fetch('/api/anonymizer-auth/login', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email, password }),
    }));
}

export async function refreshLogin(): Promise<AnonymizerAuthResponse | null> {
    const response = await fetch('/api/anonymizer-auth/refresh', { method: 'POST', credentials: 'same-origin' });
    if (response.status === 401) return null;
    return parseJsonResponse<AnonymizerAuthResponse>(response);
}

export async function logout(): Promise<void> {
    const response = await fetch('/api/anonymizer-auth/logout', { method: 'POST', credentials: 'same-origin' });
    if (!response.ok) throw new Error('Uitloggen is niet gelukt. Probeer het opnieuw.');
}

let accessToken: string | null = null;
let onSessionExpired: (() => void) | null = null;
export function setAccessToken(token: string | null): void { accessToken = token; }
export function setSessionExpiredHandler(handler: (() => void) | null): void { onSessionExpired = handler; }

async function authorizedFetch(url: string, init: RequestInit): Promise<Response> {
    const send = (token: string | null) => fetch(url, {
        ...init,
        headers: { ...init.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    let response = await send(accessToken);
    if (response.status === 401 && accessToken) {
        const renewed = await refreshLogin();
        setAccessToken(renewed?.token ?? null);
        if (renewed) response = await send(renewed.token);
        else onSessionExpired?.();
    }
    return response;
}

export async function copyToClipboard(value: string): Promise<void> {
    try {
        await navigator.clipboard.writeText(value);
    } catch {
        const helper = document.createElement('textarea');
        helper.value = value;
        helper.setAttribute('readonly', 'true');
        helper.style.position = 'absolute';
        helper.style.left = '-9999px';
        document.body.appendChild(helper);
        helper.select();
        document.execCommand('copy');
        document.body.removeChild(helper);
    }
}

export async function fetchUsage(url: string): Promise<AnonymizerUsageResult> {
    const response = await authorizedFetch(url, {
        headers: {
            Accept: 'application/json',
        },
    });

    return parseJsonResponse<AnonymizerUsageResult>(response);
}

export async function requestAnonymization(url: string, request: AnonymizationRequest): Promise<AnonymizerResult> {
    const response = await authorizedFetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify(request),
    });

    return parseJsonResponse<AnonymizerResult>(response);
}

export async function requestPersonalization(url: string, request: PersonalizationRequest): Promise<PersonalizationResponse> {
    const response = await authorizedFetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify(request),
    });

    return parseJsonResponse<PersonalizationResponse>(response);
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
    const payload = await response.json().catch(() => null);

    if (!response.ok) {
        throw createWorkspaceRequestError(response.status, payload);
    }

    return payload as T;
}

export function getWorkspaceErrorMessage(error: unknown, fallbackMessage: string): string {
    if (typeof error === 'string' && error.trim()) {
        return error;
    }

    if (error instanceof Error && error.message.trim()) {
        return error.message;
    }

    return fallbackMessage;
}

export function getWorkspaceErrorUsage(error: unknown): AnonymizerUsageResult | null {
    if (!error || typeof error !== 'object') {
        return null;
    }

    const requestError = error as WorkspaceRequestError;

    if (isUsagePayload(requestError.usage)) {
        return requestError.usage;
    }

    if (isUsagePayload(error)) {
        return error;
    }

    const nestedUsage = (error as { usage?: unknown }).usage;

    return isUsagePayload(nestedUsage) ? nestedUsage : null;
}

function createWorkspaceRequestError(status: number, payload: unknown): WorkspaceRequestError {
    const error = new Error(resolveErrorMessage(payload, status)) as WorkspaceRequestError;
    error.status = status;
    error.usage = extractUsage(payload);

    return error;
}

function resolveErrorMessage(payload: unknown, _status: number): string {
    if (typeof payload === 'string' && payload.trim()) {
        return payload;
    }

    if (payload && typeof payload === 'object') {
        const messageCarrier = payload as {
            message?: unknown;
            error?: unknown;
            title?: unknown;
            detail?: unknown;
        };

        for (const value of [messageCarrier.message, messageCarrier.error, messageCarrier.title, messageCarrier.detail]) {
            if (typeof value === 'string' && value.trim()) {
                return value;
            }
        }
    }

    return '';
}

function extractUsage(payload: unknown): AnonymizerUsageResult | null {
    if (isUsagePayload(payload)) {
        return payload;
    }

    if (payload && typeof payload === 'object') {
        const nestedUsage = (payload as { usage?: unknown }).usage;

        if (isUsagePayload(nestedUsage)) {
            return nestedUsage;
        }
    }

    return null;
}

function isUsagePayload(value: unknown): value is AnonymizerUsageResult {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const candidate = value as Partial<AnonymizerUsageResult>;

    return (typeof candidate.limit === 'number' || candidate.limit === null)
        && typeof candidate.used === 'number'
        && (typeof candidate.remaining === 'number' || candidate.remaining === null)
        && typeof candidate.isExhausted === 'boolean';
}
